// UNPROFITABLE Theme JavaScript - Optimized Version

// ============================================
// UTILITY FUNCTIONS
// ============================================

// Debounce function for performance
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

// Better scroll locking for modals
function lockScroll() {
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  document.body.style.overflow = 'hidden';
  document.body.style.paddingRight = `${scrollbarWidth}px`;
}

function unlockScroll() {
  document.body.style.overflow = '';
  document.body.style.paddingRight = '';
}

// Focus trap for modals (accessibility)
function trapFocus(element) {
  const focusableElements = element.querySelectorAll(
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  
  if (focusableElements.length === 0) return null;
  
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  
  const handleTab = (e) => {
    if (e.key === 'Tab') {
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
    // ESC to close
    if (e.key === 'Escape') {
      const closeButton = element.querySelector('.modal-close, [data-modal-close]');
      if (closeButton) closeButton.click();
    }
  };
  
  element.addEventListener('keydown', handleTab);
  
  // Focus first element
  if (firstElement) firstElement.focus();
  
  return () => element.removeEventListener('keydown', handleTab);
}

// Image error handling
function initImageErrorHandling() {
  document.querySelectorAll('img').forEach(img => {
    if (!img.hasAttribute('data-error-handled')) {
      img.setAttribute('data-error-handled', 'true');
      img.addEventListener('error', function() {
        console.warn('Image failed to load:', this.src);
        this.style.opacity = '0.5';
        this.alt = this.alt || 'Image unavailable';
      });
    }
  });
}

// ============================================
// INITIALIZATION
// ============================================

document.addEventListener('DOMContentLoaded', function() {
  // Initialize all components
  setTimeout(createTradingChart, 100);
  initMobileMenu();
  initSmoothScroll();
  initScrollAnimations();
  initModal();
  initTwitterTicker();
  initImageErrorHandling();
  
  // Redraw chart on resize with debouncing for performance
  window.addEventListener('resize', debounce(function() {
    const chartContainer = document.getElementById('trading-chart');
    if (chartContainer) {
      chartContainer.innerHTML = '';
      createTradingChart();
    }
  }, 250));
});

// ============================================
// COMPONENT FUNCTIONS
// ============================================

// Mobile Menu
function initMobileMenu() {
  const toggle = document.getElementById('mobile-menu-toggle');
  const menu = document.getElementById('mobile-menu');
  const menuIcon = document.getElementById('menu-icon');
  const closeIcon = document.getElementById('close-icon');
  
  if (!toggle || !menu) return;
  
  const toggleMenu = () => {
    const isVisible = !menu.classList.contains('hidden');
    
    if (isVisible) {
      menu.classList.add('hidden');
      menuIcon.classList.remove('hidden');
      closeIcon.classList.add('hidden');
      unlockScroll();
    } else {
      menu.classList.remove('hidden');
      menuIcon.classList.add('hidden');
      closeIcon.classList.remove('hidden');
      lockScroll();
    }
  };
  
  toggle.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleMenu();
  });
  
  const menuLinks = menu.querySelectorAll('a');
  menuLinks.forEach(link => {
    link.addEventListener('click', () => toggleMenu());
  });
  
  document.addEventListener('click', (e) => {
    const isVisible = !menu.classList.contains('hidden');
    if (isVisible && !menu.contains(e.target) && e.target !== toggle) {
      toggleMenu();
    }
  });
}

// Smooth Scrolling
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function(e) {
      e.preventDefault();
      const targetId = this.getAttribute('href');
      if (!targetId || targetId === '#') return;
      
      const targetElement = document.querySelector(targetId);
      if (targetElement) {
        // Check for smooth scroll support
        if ('scrollBehavior' in document.documentElement.style) {
          window.scrollTo({
            top: targetElement.offsetTop - 80,
            behavior: 'smooth'
          });
        } else {
          // Fallback for older browsers
          window.scrollTo(0, targetElement.offsetTop - 80);
        }
      }
    });
  });
}

// Scroll Animations
function initScrollAnimations() {
  // Check for IntersectionObserver support
  if (!('IntersectionObserver' in window)) {
    // Fallback: just show all sections
    document.querySelectorAll('section').forEach(section => {
      section.classList.add('animate-fadeIn');
    });
    return;
  }
  
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-fadeIn');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1 });
  
  document.querySelectorAll('section').forEach(section => observer.observe(section));
}

// Modal
let focusTrapCleanup = null;

function initModal() {
  const modal = document.getElementById('apply-modal');
  const applyButton = document.getElementById('apply-button');
  const boardApplyButton = document.getElementById('board-apply-button');
  const closeButton = document.getElementById('close-modal');
  const form = document.getElementById('application-form');
  const successMsg = document.querySelector('.form-success');
  const errorMsg = document.querySelector('.form-error');
  
  if (!modal) return;
  
  const closeModal = () => {
    modal.classList.remove('active');
    unlockScroll();
    if (successMsg) successMsg.style.display = 'none';
    if (errorMsg) errorMsg.style.display = 'none';
    if (form) form.reset();
    
    // Remove focus trap
    if (focusTrapCleanup) {
      focusTrapCleanup();
      focusTrapCleanup = null;
    }
  };
  
  const openModal = () => {
    modal.classList.add('active');
    lockScroll();
    
    // Add focus trap
    const modalContent = modal.querySelector('.modal-content');
    if (modalContent) {
      focusTrapCleanup = trapFocus(modalContent);
    }
  };
  
  if (applyButton) applyButton.addEventListener('click', (e) => { e.preventDefault(); openModal(); });
  if (boardApplyButton) boardApplyButton.addEventListener('click', (e) => { e.preventDefault(); openModal(); });
  if (closeButton) closeButton.addEventListener('click', closeModal);
  
  modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });
  
  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitButton = form.querySelector('button[type="submit"]');
      if (submitButton) submitButton.disabled = true;
      
      try {
        const response = await fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          headers: { 'Accept': 'application/json' }
        });
        
        if (response.ok) {
          if (successMsg) successMsg.style.display = 'block';
          if (errorMsg) errorMsg.style.display = 'none';
          setTimeout(closeModal, 2000);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (error) {
        console.error('Form submission error:', error);
        if (errorMsg) errorMsg.style.display = 'block';
        if (successMsg) successMsg.style.display = 'none';
      } finally {
        if (submitButton) submitButton.disabled = false;
      }
    });
  }
}

// Twitter Ticker
function initTwitterTicker() {
  const ticker = document.querySelector('.twitter-ticker');
  if (!ticker) return;
  
  const tickerRows = ticker.querySelectorAll('.ticker-row');
  
  // Duplicate content for seamless scrolling
  tickerRows.forEach(row => {
    const rowContent = row.innerHTML;
    row.innerHTML = rowContent + rowContent;
  });
  
  // Open Twitter on click
  ticker.addEventListener('click', () => {
    window.open('https://x.com/unprofitablevc', '_blank', 'noopener,noreferrer');
  });
  
  // Dynamic animation timing based on content length
  tickerRows.forEach((row, index) => {
    const tickerItems = row.querySelectorAll('.ticker-item');
    const contentLength = tickerItems.length / 2;
    const baseDuration = 10 + (index * 5);
    const dynamicDuration = baseDuration + (contentLength * 0.15);
    
    row.style.animationDuration = `${dynamicDuration}s`;
    row.style.animationDelay = `${-(dynamicDuration / 5) * index}s`;
  });
}

// Trading Chart
function createTradingChart() {
  const chartContainer = document.getElementById('trading-chart');
  if (!chartContainer || chartContainer.querySelector('svg')) return;
  
  // Ensure minimum height
  if (chartContainer.clientHeight < 200) {
    chartContainer.style.height = window.innerWidth <= 768 ? '300px' : '400px';
  }
  
  const containerWidth = chartContainer.clientWidth || window.innerWidth;
  const containerHeight = chartContainer.clientHeight || 400;
  
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.setAttribute('viewBox', `0 0 ${containerWidth} ${containerHeight}`);
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  svg.style.position = 'absolute';
  
  const numPoints = 24;
  const points = [];
  const startX = containerWidth * 0.05;
  const startY = containerHeight * 0.05;
  
  const generateVolatility = () => {
    const patterns = [];
    let cumulative = 0;
    
    for (let i = 0; i < numPoints; i++) {
      const progress = i / (numPoints - 1);
      const downBias = 0.3 + (progress * 0.4);
      const volatility = (progress < 0.15 || progress > 0.85) ? 0.05 : 0.12;
      
      let movement;
      if (cumulative > 0.15) {
        movement = -Math.random() * 0.15 - 0.05;
      } else if (progress > 0.4 && progress < 0.6 && Math.random() < 0.5) {
        movement = -Math.random() * 0.25;
      } else {
        movement = (Math.random() - downBias) * volatility;
      }
      
      cumulative += movement;
      if (progress > 0.85) movement -= progress * 0.15;
      patterns.push(movement);
    }
    return patterns;
  };
  
  const volatility = generateVolatility();
  const baseFunction = (t) => t < 0.6 ? t * 0.35 : 0.21 + (t - 0.6) * 1.7;
  
  for (let i = 0; i < numPoints; i++) {
    const progress = i / (numPoints - 1);
    const x = startX + progress * (containerWidth * 0.9);
    const baseY = startY + (containerHeight * 0.85) * baseFunction(progress);
    const adjustment = volatility[i] * containerHeight;
    const y = Math.max(startY, Math.min(containerHeight * 0.95, baseY + adjustment));
    points.push({ x, y });
  }
  
  let pathData = `M ${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const cp1x = points[i-1].x + (points[i].x - points[i-1].x) * 0.4;
    const cp1y = points[i-1].y + (points[i].y - points[i-1].y) * 0.2;
    const cp2x = points[i-1].x + (points[i].x - points[i-1].x) * 0.6;
    const cp2y = points[i].y - (points[i].y - points[i-1].y) * 0.2;
    pathData += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${points[i].x},${points[i].y}`;
  }
  
  // Create gradient
  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
  const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
  gradient.setAttribute('id', 'redGradient');
  gradient.setAttribute('x1', '0%');
  gradient.setAttribute('y1', '0%');
  gradient.setAttribute('x2', '0%');
  gradient.setAttribute('y2', '100%');
  
  const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
  stop1.setAttribute('offset', '0%');
  stop1.setAttribute('stop-color', '#ff0000');
  stop1.setAttribute('stop-opacity', '0.7');
  
  const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
  stop2.setAttribute('offset', '100%');
  stop2.setAttribute('stop-color', '#ff0000');
  stop2.setAttribute('stop-opacity', '0.1');
  
  gradient.appendChild(stop1);
  gradient.appendChild(stop2);
  defs.appendChild(gradient);
  svg.appendChild(defs);
  
  // Area fill
  const areaPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  let areaData = pathData + ` L ${points[points.length-1].x},${containerHeight} L ${points[0].x},${containerHeight} Z`;
  areaPath.setAttribute('d', areaData);
  areaPath.setAttribute('fill', 'url(#redGradient)');
  areaPath.setAttribute('opacity', '0.25');
  areaPath.classList.add('animate-fadeIn');
  svg.appendChild(areaPath);
  
  // Chart line
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.classList.add('chart-line', 'animate-drawLine');
  path.setAttribute('d', pathData);
  svg.appendChild(path);
  
  // Find extrema for dots
  const findExtrema = (pts, windowSize = 3) => {
    const extrema = [];
    for (let i = windowSize; i < pts.length - windowSize; i++) {
      let isMax = true, isMin = true;
      for (let j = i - windowSize; j <= i + windowSize; j++) {
        if (j !== i && pts[j].y < pts[i].y) isMax = false;
        if (j !== i && pts[j].y > pts[i].y) isMin = false;
      }
      if (isMax || isMin) extrema.push(i);
    }
    extrema.push(0, pts.length - 1);
    return [...new Set(extrema)].sort((a, b) => a - b);
  };
  
  // Add dots at extrema
  findExtrema(points).forEach(index => {
    const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    dot.setAttribute('cx', points[index].x);
    dot.setAttribute('cy', points[index].y);
    dot.setAttribute('r', 4);
    dot.classList.add('chart-dot', 'animate-pulsate');
    dot.style.animationDelay = `${index * 0.2}s`;
    svg.appendChild(dot);
  });
  
  chartContainer.appendChild(svg);
}

// ============================================
// ERROR TRACKING & MONITORING
// ============================================

// Enhanced error tracking
window.addEventListener('error', (event) => {
  console.error('Global error:', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error
  });
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', {
    reason: event.reason,
    promise: event.promise
  });
});
